/**
 * The guide route's answer — shared by the server (services/api epg-xmltv.ts)
 * and the APK (apps/web src/api/deviceApi.ts), so both return the same
 * programmes, matched to the same channels, for the same request.
 *
 * What differs is only where the parsed feed comes from and how long it is
 * kept: the server downloads a viewer's feed and caches it; the APK downloads
 * it straight from the viewer's own provider, with no server in between
 * (Section 22 Phase 1). Everything between "a parsed feed" and "the JSON the
 * app receives" lives here.
 */
/** Hours of EPG lookback included beyond the requested window, so long-running
 * programs (3h+ sports events) that started before the guide's windowStart
 * still render in the grid instead of leaving a gap at the left edge. */
export const WINDOW_BACK_HOURS = 3;
/** Hours of EPG lookahead beyond the requested window — absorbs clock skew
 * between server and client and programs that straddle the right edge. */
export const WINDOW_FWD_HOURS = 2;
/** Programme retention after a parse, in hours from now. See trimToRetention. */
export const DEFAULT_RETENTION_HOURS = 48;
/** Read and validate a guide request body. Throws on an invalid filter. */
export function readGuideRequest(body) {
    const value = body && typeof body === "object" ? body : {};
    const channelIds = parseChannelIds(body);
    const channelNames = parseChannelNames(body);
    return {
        channelIds,
        channelNames,
        refresh: value.refresh === true,
        includeDetails: value.includeDetails !== false,
        windowHours: parseWindowHours(value),
    };
}
/**
 * True when a downloaded feed is a whole document. A feed cut off mid-document
 * (a gateway error page, a dropped connection) still parses into a smaller but
 * valid guide, and caching that partial parse as "the guide" would hide most
 * channels' programmes until it expired.
 */
export function isCompleteXmltv(raw) {
    return /<\/tv>\s*$/i.test(raw);
}
/**
 * The answer for a playlist that names no guide and has none typed in: an
 * empty guide, not an error. Most public playlists carry no guide, and their
 * channels must still list and play — an error here read as a broken provider
 * ("The provider returned an invalid response") when nothing was wrong.
 */
export function emptyGuideResponse(request) {
    return buildGuideResponse({ programs: [], channels: [], feedChannels: 0, fetchedAt: new Date().toISOString() }, request, { epgUrl: "", cached: false });
}
/**
 * The guide route's response body for one request against a parsed feed.
 *
 * Contract: `channelIds` are XMLTV channel ids; `channelNames` are catalog
 * names, matched to feed channels by matchFeedChannelsByName, and
 * `nameMapping` tells the app which of its channels each feed channel backs.
 * The feed's channel stubs are always included. `epgUrl` is echoed with its
 * credentials removed.
 */
export function buildGuideResponse(feed, request, meta) {
    const { channelIds, channelNames, includeDetails, windowHours } = request;
    let programs;
    // XMLTV channelId → the original catalog names the client sent, so the app
    // can remap programmes to its own channel ids without re-normalising.
    const nameMapping = {};
    if (channelNames) {
        const match = matchFeedChannelsByName(feed.channels, channelNames);
        programs = feed.programs.filter((p) => match.wantedIds.has(p.channelId));
        // Copied: the match is shared with later identical requests.
        for (const [xmltvId, requestedNames] of match.nameMapping)
            nameMapping[xmltvId] = [...requestedNames];
    }
    else if (channelIds) {
        const wanted = new Set(channelIds);
        programs = feed.programs.filter((p) => wanted.has(p.channelId));
    }
    else {
        programs = feed.programs;
    }
    // Time-window filter: a 4/12/24h guide only ships programmes overlapping
    // that window (plus small margins), not each channel's whole retained feed.
    if (windowHours !== null)
        programs = filterProgramsByWindow(programs, windowHours);
    programs = programs.map((p) => (includeDetails ? p : slimProgram(p)));
    const requestedVia = channelNames ? "names" : channelIds ? "ids" : "all";
    const matchedChannels = (channelIds ?? channelNames) ? [...new Set(programs.map((p) => p.channelId))] : [];
    return {
        source: "xmltv",
        epgUrl: redactEpgUrl(meta.epgUrl),
        windowHours,
        feedChannels: feed.feedChannels,
        requestedChannels: channelIds ?? channelNames ?? null,
        matchedChannels,
        requestedVia,
        nameMapping,
        channels: feed.channels,
        programs,
        cached: meta.cached,
        fetchedAt: feed.fetchedAt,
    };
}
/**
 * Raised whenever the matching rules change, so a mapping saved by an older
 * matcher (the APK keeps one per guide download) is worked out again rather
 * than kept until the next download. 3: numbered event slots (2026-10-23).
 */
export const NAME_MATCH_VERSION = 3;
/**
 * Match catalog channel names to the feed's channels. Catalog names ("USA◈ Fox
 * News HD") and feed names ("USA Fox News UHD") differ in punctuation, country
 * prefixes and quality suffixes, so both are normalised, then candidates are
 * ranked by rarity-weighted token overlap with a US-feed preference.
 *
 * The result is shared and must be treated as read-only: identical requests
 * against the same parsed feed are answered from memory (see createFeedMatcher).
 */
export function matchFeedChannelsByName(feedChannels, channelNames) {
    let matcher = feedMatchers.get(feedChannels);
    if (!matcher) {
        matcher = createFeedMatcher(feedChannels);
        feedMatchers.set(feedChannels, matcher);
    }
    return matcher(channelNames);
}
/** One matcher per parsed feed, keyed by the feed's channel list, so it is
 * released with the feed when a refresh replaces it. */
const feedMatchers = new WeakMap();
/** Answers kept per feed. Home, Live and the background sync ask with the same
 * ~10k names; a few slots cover them and a category or two. */
const MATCH_RESULTS_KEPT = 4;
function memoize(fn) {
    const cache = new Map();
    return (s) => {
        if (cache.has(s))
            return cache.get(s);
        const value = fn(s);
        cache.set(s, value);
        return value;
    };
}
/**
 * The matcher for one parsed feed. Everything that depends only on the feed —
 * its indexes, token list, each name's normalised forms — is built ONCE here,
 * not per requested name. Matching this provider's 10,777 catalog names took
 * ~12 s per request on a phone when every name re-normalised hundreds of feed
 * names, re-copied the full token list and re-scored candidates inside the
 * sort comparator. The helpers are pure, so remembering them changes no result.
 */
function createFeedMatcher(feedChannels) {
    const entry = { channels: feedChannels };
    // Name-based matching with normalization.
    // Catalog names ("USA◈ Fox News HD") and XMLTV names ("USA Fox News UHD")
    // use different punctuation/affixes — normalize both before comparing.
    // Decompose unicode superscripts/small-caps to ASCII before any other
    // processing: feed names like "[US] SPORTSGRID FHD [sᴛᴠ+]" use ᴛ/ᴠ so
    // they don't become stray single letters ("s") that poison matching.
    // NFKD handles accents ("español" → "espanol") and modifier letters;
    // small caps (ᴛᴠ, which have no decomposition) are mapped explicitly.
    const SMALL_CAPS = {
        "ᴀ": "a", "ᴄ": "c", "ᴅ": "d", "ᴇ": "e", "ꜰ": "f", "ɢ": "g", "ʜ": "h",
        "ɪ": "i", "ᴊ": "j", "ᴋ": "k", "ʟ": "l", "ᴍ": "m", "ɴ": "n", "ᴏ": "o",
        "ᴘ": "p", "ʀ": "r", "ꜱ": "s", "ᴛ": "t", "ᴜ": "u", "ᴠ": "v", "ᴡ": "w",
        "ʏ": "y", "ᴢ": "z",
    };
    const deAccent = (s) => s.normalize("NFKD").replace(/[\u0300-\u036f]/g, "").replace(/[ᴀᴄᴅᴇꜰɢʜɪᴊᴋʟᴍɴᴏᴘʀꜱᴛᴜᴠᴡʏᴢ]/g, (ch) => SMALL_CAPS[ch] ?? ch);
    const normalize = memoize((s) => deAccent(s).toLowerCase()
        .replace(/[^\w\s]/g, " ") // strip special chars
        .replace(/\b(us|usa|uk|ca|mx|au|nz)\b/gi, "") // strip country prefixes
        .replace(/\b(hd|fhd|uhd|4k|sd|hevc)\b/gi, "") // strip quality suffixes
        .replace(/\s+/g, " ") // collapse whitespace
        .trim());
    // Word tokens that KEEP country/quality markers (unlike normalize), so
    // "USA ESPN" can be told apart from "MX| ESPN HD" when both collapse
    // to the same normalized key.
    const tokens = memoize((s) => deAccent(s).toLowerCase().replace(/[^\w\s]/g, " ").split(/\s+/).filter(Boolean));
    const hasUsMarker = (s) => /\b(us|usa)\b/i.test(s);
    // Stopwords + numbers never count as shared tokens for the fuzzy tier:
    // "The Rifleman" must not match "ESPN8 The Ocho" on "the", and a
    // local "Bowling Green" NBC station must not match "Bowling TV".
    const STOPWORDS = new Set(["the", "and", "of", "on", "at", "by", "in", "to", "for", "with", "an", "a", "as", "is", "it", "or", "via", "vs", "la", "el", "los", "las", "de", "del", "y"]);
    // Words that carry NO identity: pure descriptors bolted onto names of
    // channels from every network ("Sports Network", "ESPN HD", "News TV").
    // They neither help match nor hurt when missing, so the catalog's
    // "USA MLB Network" still resolves to the feed's "USA MLB UHD", and
    // "USA ESPN BACKUP" stays on the main espn.us feed.
    const GENERIC = new Set(["hd", "fhd", "uhd", "4k", "sd", "hevc", "tv", "channel", "network", "backup", "stv", "pc", "usa", "us"]);
    // Identity-bearing tokens used to find candidate feed channels. Pure
    // digits are excluded here (handled by scoring) so "ESPN 2" seeds on
    // "espn".
    const brandTokens = memoize((s) => tokens(s).filter((t) => t.length >= 3 && !STOPWORDS.has(t) && !/^\d+$/.test(t) && !GENERIC.has(t)));
    // Scoring tokens: everything except stopwords and generic descriptors.
    // Keeps digits and short words ("2" in "ESPN 2", "u" in "ESPN U") so
    // an exact number match is decisive ("FOX SPORTS 1" ≠ "FOX SPORTS 3")
    // and "USA ESPN U" prefers the espnu.us feed over plain espn.us.
    const scoreTokens = memoize((s) => tokens(s).filter((t) => !STOPWORDS.has(t) && !GENERIC.has(t)));
    // Event words. A catalog channel named for an EVENT or a pay-per-view ("HBO
    // Boxing", "ESPN PPV 3", "Fight Night Events") is a different channel from the
    // network it borrows its brand from, and the network's guide is wrong for it:
    // "HBO Boxing" was showing HBO's films (owner, 2026-09). So a candidate that
    // lacks the request's event word is never its match. Unlike "BeIN Sports La
    // Liga" → beIN Sports, which is meant (that test stays), there is no better
    // guide than none here, and "Guide from channel names" (playbook Phase 3) is
    // the planned answer for event channels.
    const EVENT_WORDS = new Set(["boxing", "ppv", "event", "events", "fight", "fights", "mma", "wrestling", "ufc", "bellator"]);
    const lacksEventWord = (requested, displayName) => {
        const cand = tokens(normalize(displayName));
        return tokens(normalize(requested)).some((t) => EVENT_WORDS.has(t) && !cand.some((c) => tokensMatch(c, t)));
    };
    // Numbered event slots (onn box, 2026-10-23): providers carry streaming
    // events in numbered slots ("ESPN+ 12", ":SEC+ 06", ":MAX US 03"), filled with
    // whatever is on that day. Stripped of its "+", ":SEC+ 06" was given SEC
    // Network's listing, and ":MAX US 03" a French channel called MAX. A slot is
    // named by a number after a "+" brand, or by a zero-padded number, and a
    // candidate must carry the same number to be its match: "beIN Sports 03"
    // still finds "beIN Sports 3", and a slot with no listing of its own gets no
    // guide rather than a wrong one. Times and dates in a filled slot's name
    // ("... @ Oct 22 3:05 PM :SEC+ 03") are left out of the search for it. A
    // slot past 09 (":MAX US 10") carries no zero, so it is known by its series:
    // the same name with a zero-padded number among the names asked for.
    const slotPart = memoize((s) => {
        const bare = deAccent(s)
            .toLowerCase()
            .replace(/^.*@.*?\d\s*[ap]\.?m\.?/, " ")
            .replace(/\d{4}-\d{1,2}-\d{1,2}|\d{1,2}:\d{2}/g, " ");
        const marked = /\+\s*(\d{1,3})(?!\d)/.exec(bare) ?? /(?:^|[^\d])(0\d)(?!\d)/.exec(bare);
        if (marked)
            return { slot: Number(marked[1]), padded: true, series: seriesOf(bare, marked) };
        const plain = /(?:^|[^\d])(\d{2})(?!\d)/.exec(bare);
        return plain ? { slot: Number(plain[1]), padded: false, series: seriesOf(bare, plain) } : null;
    });
    const seriesOf = (bare, found) => (bare.slice(0, found.index + found[0].length - found[1].length) + "#" + bare.slice(found.index + found[0].length))
        .replace(/[^a-z#]+/g, " ")
        .trim();
    const slotSeries = (channelNames) => {
        const series = new Set();
        for (const name of channelNames) {
            const part = slotPart(name);
            if (part?.padded)
                series.add(part.series);
        }
        return series;
    };
    const lacksSlotNumber = (requested, displayName, series) => {
        const part = slotPart(requested);
        if (!part || (!part.padded && !series.has(part.series)))
            return false;
        return !tokens(displayName).some((t) => /^\d+$/.test(t) && Number(t) === part.slot);
    };
    // Tokens match when equal, or when one contains the other (both long
    // enough to be meaningful): "players tv" ↔ "playerstv", "nfl red
    // zone" ↔ "nflredzone", "draftkings" ↔ "draft kings".
    const tokensMatch = (a, b) => a === b || (a.length >= 3 && b.length >= 3 && (a.includes(b) || b.includes(a)));
    // Bridging for the SEED fallback is stricter than tokensMatch: only
    // PREFIX relationships qualify, and only when both sides are ≥ 4 chars.
    // Full containment would let "ted" (a substring of "illustrated") pull
    // TED into the candidate set for "Sports Illustrated TV". Prefix-only
    // still bridges "playerstv" ↔ "players" and "draft" → "draftkings"
    // while refusing interior-substring accidents.
    const seedBridges = (a, b) => a === b || (a.length >= 4 && b.length >= 4 && (a.startsWith(b) || b.startsWith(a)));
    // Rarity-weighted token overlap. Distinctive tokens (network names:
    // "bein", "tnt", "espn") appear in few feed channels and must count
    // far more than generic ones ("sports", "tv", "network", "news").
    // weight(t): rare in the feed → high. Tokens ABSENT from the feed get
    // the highest weight, so a candidate that lacks a request brand token
    // is rejected ("USA SLVR Sports" → "PX Sports" shares only "sports"
    // and loses on the missing "slvr").
    const weightOf = (t) => {
        const count = tokenCount.get(t) ?? 1;
        const w = Math.log2((feedChannelCount ?? 1) / count + 1);
        return w > 0.75 ? w : 0.3;
    };
    // Penalty for a REQUEST token the candidate lacks. Observed tokens
    // keep their rarity weight; tokens ABSENT from the whole feed (the
    // provider renamed the channel, e.g. "Sports Illustrated TV" → "PX
    // Sports", "FNTSY" → "GAME+", "Premier Lacrosse" → "PLL") get a
    // fixed moderate cost so niche channels still resolve to their closest
    // real feed instead of returning no data at all.
    const missingWeight = (t) => {
        const count = tokenCount.get(t);
        return count === undefined ? 4 : weightOf(t);
    };
    const overlapScore = (requested, displayName) => {
        const req = scoreTokens(normalize(requested));
        const cand = scoreTokens(normalize(displayName));
        const reqSet = new Set(req);
        const candSet = new Set(cand);
        let score = 0;
        for (const t of candSet) {
            if ([...reqSet].some((r) => tokensMatch(t, r))) {
                score += weightOf(t);
            }
            else {
                // Candidate introduces a token the request never asked for. If
                // it's distinctive (a different brand), cost its weight — a
                // local "Bowling Green" NBC station costs "green"+"nbc" and
                // loses to nothing. Single-char strays (the "s" left over from
                // "[sᴛᴠ+]") are noise and cost a flat 0.5.
                const w = weightOf(t);
                score -= w >= 2.5 && t.length > 2 ? w + 1 : 0.5;
            }
        }
        for (const t of reqSet) {
            if (![...candSet].some((c) => tokensMatch(t, c)))
                score -= missingWeight(t);
        }
        return score;
    };
    // A candidate whose full name is a substring of the request (or vice
    // versa) shares its identity — "Sports | USA Network (US)" contains
    // the request "USA Network".
    const substringBonus = (requested, displayName) => {
        const r = tokens(requested).join(" ");
        const c = tokens(displayName).join(" ");
        return r.includes(c) || c.includes(r) ? 2.5 : 0;
    };
    const scoreCandidate = (requested, displayName, id) => {
        const score = overlapScore(requested, displayName);
        const us = hasUsMarker(requested)
            ? hasUsMarker(displayName) || id.endsWith(".us")
                ? 6
                : -6
            : 0;
        return score + us + substringBonus(requested, displayName);
    };
    // Keep ALL candidates per normalized key (not one) so a collision like
    // "USA ESPN" / "USA ESPN UHD" / "MX| ESPN HD" → "espn" doesn't drop
    // the US feed in favor of a foreign one via last-write-wins.
    const nameToCandidates = new Map();
    // Token index for the fuzzy tier: normalized token → feed channels
    // that contain it. Lets "USA TNT Sports" find the "USA TNT UHD" feed
    // even though their normalized keys ("tnt sports" vs "tnt") never
    // collide. Indexed by NORMALIZED tokens so noise like "usa"/"hd"
    // doesn't dominate the seed.
    const tokenToCandidates = new Map();
    for (const ch of entry.channels) {
        if (!ch.displayName)
            continue;
        const norm = normalize(ch.displayName);
        if (!norm)
            continue;
        const list = nameToCandidates.get(norm) ?? [];
        list.push({ id: ch.id, displayName: ch.displayName });
        nameToCandidates.set(norm, list);
        for (const t of brandTokens(norm)) {
            const tl = tokenToCandidates.get(t) ?? [];
            tl.push({ id: ch.id, displayName: ch.displayName });
            tokenToCandidates.set(t, tl);
        }
    }
    // Rarity counts backing weightOf: how many feed channels carry each
    // brand token. Distinctive network brands ("bein", "tnt") are rare;
    // generic words ("sports", "news") are common.
    const feedChannelCount = entry.channels.filter((c) => c.displayName).length;
    const tokenCount = new Map();
    for (const ch of entry.channels) {
        if (!ch.displayName)
            continue;
        for (const t of brandTokens(normalize(ch.displayName))) {
            tokenCount.set(t, (tokenCount.get(t) ?? 0) + 1);
        }
    }
    // All index keys, for the prefix-bridge fallback below — once per feed.
    const tokenKeys = [...tokenToCandidates.keys()];
    // Prefix-bridged candidates per rare token; they depend only on the token.
    const bridged = new Map();
    // Recent answers, by the exact list of names asked for.
    const results = new Map();
    return (channelNames) => {
        const resultKey = JSON.stringify(channelNames);
        const known = results.get(resultKey);
        if (known)
            return known;
        const nameToRequestedName = new Map();
        const wantedIds = new Set();
        const series = slotSeries(channelNames);
        for (const name of channelNames) {
            // Gather candidates from BOTH tiers and let the scorer pick the best
            // across them — exact keys first, then fuzzy token overlap. Merging
            // means a US feed can out-score a foreign feed that happens to have
            // an exact normalized key ("USA MLB UHD" vs "CA MLB Network").
            const norm = normalize(name);
            const exact = nameToCandidates.get(norm) ?? [];
            const all = new Map();
            for (const cand of exact)
                all.set(cand.id, cand);
            const reqTokens = brandTokens(norm);
            if (reqTokens.length >= 1) {
                // Seed from the rarest token (fewest feed candidates) to bound the
                // scan, then keep channels whose brand-token set overlaps. Brand
                // tokens exclude stopwords/numbers/generic descriptors, and the
                // overlap is containment-aware ("players tv" ↔ "playerstv"). The
                // overlapScore guard below rejects weak picks the gate can't see.
                // Seed from the UNION of every request brand token's candidates
                // (direct index hit, or prefix-bridged when the token is a
                // concatenation the feed splits: "playerstv" → "players",
                // "draft" → "draftkings", "poker" → "pokergo"). A single rarest-
                // token seed misses the right channel when the brand token is
                // common but a rarer league/descriptor token dominates: "BeIN
                // Sports La Liga" must still see beinsports.us (shares "bein"),
                // not just the "liga"-token channels.
                let seed = [];
                const seedSeen = new Set();
                for (const t of reqTokens) {
                    const direct = tokenToCandidates.get(t) ?? [];
                    let list = direct;
                    // Prefix-bridge ONLY for rare tokens: a concatenation like
                    // "playerstv"/"pokergo"/"draftkings" is by definition uncommon,
                    // so "poker" (2 direct hits) also brings in "pokergo" — the
                    // real PokerGO feed. Common tokens ("sports" hits 159 channels,
                    // "fox" 209) must NOT bridge, or every "sports*" channel like
                    // sportsgrid.us would compete for "USA TNT Sports" and win by
                    // matching "sports" at full weight.
                    if (direct.length <= 3) {
                        let viaContainment = bridged.get(t);
                        if (!viaContainment) {
                            viaContainment = [];
                            for (const k of tokenKeys) {
                                if (k !== t && seedBridges(k, t))
                                    viaContainment.push(...(tokenToCandidates.get(k) ?? []));
                            }
                            bridged.set(t, viaContainment);
                        }
                        list = [...direct, ...viaContainment];
                    }
                    for (const cand of list) {
                        if (!seedSeen.has(cand.id)) {
                            seedSeen.add(cand.id);
                            seed.push(cand);
                        }
                    }
                }
                const reqSet = new Set(reqTokens);
                for (const cand of seed) {
                    if (all.has(cand.id))
                        continue;
                    const candSet = new Set(brandTokens(normalize(cand.displayName)));
                    let shared = 0;
                    for (const t of candSet) {
                        if ([...reqSet].some((r) => tokensMatch(t, r)))
                            shared += 1;
                    }
                    // Require a real overlap: at least 2 shared brand tokens for
                    // multi-token requests ("TNT Sports" vs "TNT UHD"), or a single
                    // shared token when the shorter side has just one ("WWE" →
                    // "WWE NETWORK"). All but one of the shorter set must overlap,
                    // so a 3-token candidate sharing only 1 is rejected.
                    const shorter = Math.min(reqSet.size, candSet.size);
                    const minShared = shorter >= 2 ? 2 : 1;
                    if (shared >= minShared && shared >= shorter - 1)
                        all.set(cand.id, cand);
                }
            }
            // Rank candidates by score, then take the best one that clears the
            // egregious-match bar: overlap (before the US-marker tiebreak) must
            // be ≥ -4. Strongly negative scores mean the candidate shares almost
            // no identity ("Bowling TV" → a "Bowling Green" NBC affiliate) and
            // are skipped — mild negatives are kept, because a provider that
            // renamed a channel ("Sports Illustrated TV" → "PX Sports") is
            // better matched to its closest real feed than left with no data.
            // Exact-key matches bypass the bar — the normalized key already
            // proved identity.
            // Each candidate is scored once, then sorted by that score — the same
            // order the comparator produced when it re-scored both sides per compare.
            const ranked = all.size
                ? [...all.values()]
                    .map((cand) => ({ cand, score: scoreCandidate(name, cand.displayName, cand.id) }))
                    .sort((a, b) => b.score - a.score || Number(b.cand.id.endsWith(".us")) - Number(a.cand.id.endsWith(".us")))
                    .map((scored) => scored.cand)
                : [];
            const best = ranked.find((cand) => !lacksEventWord(name, cand.displayName) &&
                !lacksSlotNumber(name, cand.displayName, series) &&
                (exact.length > 0 || overlapScore(name, cand.displayName) >= -4));
            if (!best)
                continue;
            wantedIds.add(best.id);
            const names = nameToRequestedName.get(best.id) ?? [];
            if (!names.includes(name))
                names.push(name);
            nameToRequestedName.set(best.id, names);
        }
        const match = { wantedIds, nameMapping: nameToRequestedName };
        results.set(resultKey, match);
        if (results.size > MATCH_RESULTS_KEPT)
            results.delete(results.keys().next().value);
        return match;
    };
}
/** Grid-only projection: no description/subtitle, keeps genre for color-coding. */
export function slimProgram(program) {
    return {
        id: program.id,
        channelId: program.channelId,
        title: program.title,
        start: program.start,
        end: program.end,
        ...(program.genre ? { genre: program.genre } : {}),
    };
}
/** Resolve the XMLTV feed URL: explicit epgUrl > Xtream xmltv.php > get.php derivation. */
export function resolveEpgUrl(body, source) {
    if (body && typeof body === "object" && typeof body.epgUrl === "string") {
        const explicit = body.epgUrl.trim();
        if (/^https?:\/\//i.test(explicit))
            return explicit;
    }
    if (source.kind === "xtream") {
        const base = source.xtreamBaseUrl.replace(/\/+$/, "");
        const username = encodeURIComponent(source.xtreamUsername);
        const password = encodeURIComponent(source.xtreamPassword);
        return `${base}/xmltv.php?username=${username}&password=${password}`;
    }
    // M3U mode: XUI panels expose xmltv.php with the same credentials that are
    // usually in the get.php playlist URL. No extra playlist download needed.
    try {
        const url = new URL(source.m3uUrl);
        if (/get\.php$/i.test(url.pathname)) {
            const username = url.searchParams.get("username");
            const password = url.searchParams.get("password");
            if (username && password) {
                return `${url.origin}/xmltv.php?username=${encodeURIComponent(username)}&password=${encodeURIComponent(password)}`;
            }
        }
    }
    catch {
        // Falls through to null; the caller reports the missing-EPG error.
    }
    return null;
}
function parseChannelNames(body) {
    if (!body || typeof body !== "object")
        return null;
    const value = body.channelNames;
    if (!Array.isArray(value) || value.length === 0)
        return null;
    return value.filter((entry) => typeof entry === "string" && entry.length > 0);
}
/** Parse the optional guide-window request (hours of EPG around now). null =
 * no window filter (whole feed returned). Validated to a positive number;
 * the client sends 4/12/24 but any positive window is accepted. */
function parseWindowHours(body) {
    const value = body.windowHours;
    if (value === undefined || value === null)
        return null;
    const hours = Number(value);
    if (!Number.isFinite(hours) || hours <= 0) {
        throw invalidRequest("windowHours must be a positive number of hours");
    }
    // Cap absurd windows at a week — a 24h guide is the largest UI offers.
    return Math.min(hours, 168);
}
/** Keep only programs overlapping [now - WINDOW_BACK_HOURS, now + windowHours
 * + WINDOW_FWD_HOURS]. The grid anchors its windowStart at the top of the
 * current hour and extends `windowHours` forward; the back margin keeps the
 * in-progress program (which may have started well before the hour) visible,
 * and the forward margin covers the grid's right edge plus clock skew. */
export function filterProgramsByWindow(programs, windowHours) {
    const now = new Date();
    now.setMinutes(0, 0, 0);
    const windowStart = now.getTime() - WINDOW_BACK_HOURS * 3600_000;
    const windowEnd = now.getTime() + (windowHours + WINDOW_FWD_HOURS) * 3600_000;
    return programs.filter((p) => {
        const ps = new Date(p.start).getTime();
        const pe = new Date(p.end).getTime();
        return pe > windowStart && ps < windowEnd;
    });
}
function parseChannelIds(body) {
    if (!body || typeof body !== "object")
        return null;
    const value = body.channelIds;
    if (value === undefined || value === null)
        return null;
    if (!Array.isArray(value))
        throw invalidRequest("channelIds must be an array");
    // Empty array = request all channels (no filter)
    if (value.length === 0)
        return null;
    const ids = value.filter((entry) => typeof entry === "string" && /^[a-zA-Z0-9:._-]{1,80}$/.test(entry));
    if (ids.length === 0)
        throw invalidRequest("channelIds must contain valid channel ids");
    return [...new Set(ids)];
}
/** Drop programmes outside the rolling retention window. The back margin
 * exceeds WINDOW_BACK_HOURS so long-running events (3h+ sports) that began
 * before the window still render at the left edge of the grid instead of
 * leaving a gap.
 *
 * Providers ship a full WEEK of programmes; the guide never shows more than
 * ~29h of it (a 24h window plus margins), so keeping the rest only costs
 * memory — ~279MB of heap for this provider's week on the server, and a phone
 * or TV stick has far less to spare. */
export function trimToRetention(programs, retentionHours = DEFAULT_RETENTION_HOURS) {
    const now = Date.now();
    const keepFrom = now - (WINDOW_BACK_HOURS + 3) * 3600_000;
    const keepUntil = now + retentionHours * 3600_000;
    return programs.filter((program) => {
        const start = Date.parse(program.start);
        const end = Date.parse(program.end);
        // Unparseable timestamps are kept rather than silently dropped — a feed
        // quirk must never make programmes vanish from the guide.
        if (Number.isNaN(start) || Number.isNaN(end))
            return true;
        return end > keepFrom && start < keepUntil;
    });
}
/** Strip credentials from a feed URL before echoing it to the client. */
export function redactEpgUrl(value) {
    try {
        const url = new URL(value);
        if (url.username || url.password) {
            url.username = "";
            url.password = "";
        }
        for (const key of [...url.searchParams.keys()]) {
            if (/^(?:user(?:name)?|pass(?:word)?|pwd|token|auth|api[_-]?key|secret|key)$/i.test(key))
                url.searchParams.delete(key);
        }
        return url.toString();
    }
    catch {
        return value;
    }
}
function invalidRequest(message) {
    const error = new Error(message);
    error.name = "MalformedProviderResponseError";
    return error;
}
//# sourceMappingURL=guide.js.map